import { Reference } from '@ninetailed/experience.js';
import {
  ExperienceConfiguration,
  useExperience,
} from '@ninetailed/experience.js-next';
import { useMemo } from 'react';
import { NAVIGATION_ELEMENT_CONFIGS, NavigationExperiments } from './data';
import { ElementsByConfig } from './use-navigation-data';
import { getExperimentData } from './utils/experiment-helpers';

/**
 * Custom hook to manage navigation experiments
 */
export const useNavigationExperiments = (
  elementsByConfig: Map<string, ElementsByConfig>
): NavigationExperiments => {
  // Pre-calculate experiment data to avoid recalculating on each render
  const experimentDataMemo = useMemo(() => {
    const data: Record<
      string,
      { baseline: Reference; experiences: ExperienceConfiguration<Reference>[] }
    > = {};
    NAVIGATION_ELEMENT_CONFIGS.forEach((config) => {
      const key = `${config.type}_${config.petType}_${config.targetGroup}`;
      data[key] = getExperimentData({
        type: config.type,
        petType: config.petType,
        targetGroup: config.targetGroup,
        elementsByConfig,
      });
    });
    return data;
  }, [elementsByConfig]);

  // Call useExperience for each navigation element at the hook level (React Hook rules require fixed calls)
  const desktopDogNewExperiment = useExperience(
    experimentDataMemo.DESKTOP_DOG_NEW_CUSTOMER || {
      baseline: { id: '', sys: { id: '' } } as Reference,
      experiences: [] as ExperienceConfiguration<Reference>[],
    }
  );
  const desktopDogExistingExperiment = useExperience(
    experimentDataMemo.DESKTOP_DOG_EXISTING_CUSTOMER || {
      baseline: { id: '', sys: { id: '' } } as Reference,
      experiences: [] as ExperienceConfiguration<Reference>[],
    }
  );
  const desktopCatNewExperiment = useExperience(
    experimentDataMemo.DESKTOP_CAT_NEW_CUSTOMER || {
      baseline: { id: '', sys: { id: '' } } as Reference,
      experiences: [] as ExperienceConfiguration<Reference>[],
    }
  );
  const desktopCatExistingExperiment = useExperience(
    experimentDataMemo.DESKTOP_CAT_EXISTING_CUSTOMER || {
      baseline: { id: '', sys: { id: '' } } as Reference,
      experiences: [] as ExperienceConfiguration<Reference>[],
    }
  );
  const mobileDogNewExperiment = useExperience(
    experimentDataMemo.MOBILE_DOG_NEW_CUSTOMER || {
      baseline: { id: '', sys: { id: '' } } as Reference,
      experiences: [] as ExperienceConfiguration<Reference>[],
    }
  );
  const mobileDogExistingExperiment = useExperience(
    experimentDataMemo.MOBILE_DOG_EXISTING_CUSTOMER || {
      baseline: { id: '', sys: { id: '' } } as Reference,
      experiences: [] as ExperienceConfiguration<Reference>[],
    }
  );
  const mobileCatNewExperiment = useExperience(
    experimentDataMemo.MOBILE_CAT_NEW_CUSTOMER || {
      baseline: { id: '', sys: { id: '' } } as Reference,
      experiences: [] as ExperienceConfiguration<Reference>[],
    }
  );
  const mobileCatExistingExperiment = useExperience(
    experimentDataMemo.MOBILE_CAT_EXISTING_CUSTOMER || {
      baseline: { id: '', sys: { id: '' } } as Reference,
      experiences: [] as ExperienceConfiguration<Reference>[],
    }
  );

  // Create experiments map with optimized dependencies
  return useMemo(() => {
    const experimentMap: NavigationExperiments = {};

    const experimentMappings = [
      { key: 'DESKTOP_DOG_NEW_CUSTOMER', experiment: desktopDogNewExperiment },
      {
        key: 'DESKTOP_DOG_EXISTING_CUSTOMER',
        experiment: desktopDogExistingExperiment,
      },
      { key: 'DESKTOP_CAT_NEW_CUSTOMER', experiment: desktopCatNewExperiment },
      {
        key: 'DESKTOP_CAT_EXISTING_CUSTOMER',
        experiment: desktopCatExistingExperiment,
      },
      { key: 'MOBILE_DOG_NEW_CUSTOMER', experiment: mobileDogNewExperiment },
      {
        key: 'MOBILE_DOG_EXISTING_CUSTOMER',
        experiment: mobileDogExistingExperiment,
      },
      { key: 'MOBILE_CAT_NEW_CUSTOMER', experiment: mobileCatNewExperiment },
      {
        key: 'MOBILE_CAT_EXISTING_CUSTOMER',
        experiment: mobileCatExistingExperiment,
      },
    ];

    experimentMappings.forEach(({ key, experiment }) => {
      const elementData = elementsByConfig.get(key);
      if (elementData) {
        experimentMap[key] = {
          element: elementData.element,
          experiment,
        };
      }
    });

    return experimentMap;
  }, [
    elementsByConfig,
    desktopDogNewExperiment,
    desktopDogExistingExperiment,
    desktopCatNewExperiment,
    desktopCatExistingExperiment,
    mobileDogNewExperiment,
    mobileDogExistingExperiment,
    mobileCatNewExperiment,
    mobileCatExistingExperiment,
  ]);
};
